<template lang="pug">
.voicemail.max-w-prose.flex.flex-col.gap-6.duration-1000.transition-all.relative(
  :data-id="screen.id"
  :data-voicemail="screen.voicemail"
  ref="elRef"
)
  .voicemail__headshot.absolute.w-20.h-20.inset-0.rounded-full.overflow-hidden.z-10.fade-in(
    v-if="screen.headshot"
  )
    img.w-full(:src="screen.headshot")

  .m-8.mb-4.z-0.fade-in(v-if="!wordDoc && !audioDoc")
    .voicemail__player.bg-primary-500.dark_bg-gray-100.flex.flex-col.rounded-xl.p-6.items-center.self-center.auto-alpha-0(
      :class="{ 'auto-alpha-1': waveformDataURL !== '' }"
    )
      .voicemail__audioRef.value.hidden
        audio(
          @ended="onEnded"
          @pause="playing = false"
          @play="playing = true"
          crossOrigin="anonymous"
          ref="audioRef"
          v-if="canPlayAudio"
        )
          source(:key="audioSrc" :src="audioSrc" type="audio/mpeg")
      .voicemail__seek.group.w-full.h-32.-mb-4.relative.z-0(ref="seekRef")
        .fill.z-0(
          :style="{ maskImage: `url(${waveformDataURL})`, maskSize: '100% 100%', maskRepeat: 'no-repeat', maskPosition: 'center' }"
        )
          .fill.overflow-hidden.z-10
            .voicemail__seek-progress.fill.bg-primary-800.dark_bg-primary-700(
              ref="seekProgressRef"
            )
          .fill.z-0(class="bg-white/70 dark_bg-primary-500/40")
        .fill.z-10.voicemail__seek-progress.flex.items-center(
          ref="seekProgressDotRef"
          v-if="canPlayAudio"
        )
          .voicemail__seek-dot.w-4.h-4.rounded-full.bg-primary-800.dark_bg-primary-700.absolute.-right-2.transition.group-hover_ring-8(
            class="ring-primary-800/40 dark_ring-primary-700/40"
          )
        canvas.w-full.h-32.invisible(ref="canvasRef")

      mixin icon-button
        button.ripple.ripple--centered.rounded-full.transition.inline-flex.items-center.justify-center(
          class="bg-white/0 hover_bg-white/10 active_bg-white/5 dark_bg-primary-500/0 dark_hover_bg-primary-500/10 dark_active_bg-primary-500/5 dark_ripple_bg-primary-500/10"
        )&attributes(attributes)
          block

      .voicemail__controls.z-10.flex.flex-row.gap-4.justify-center.items-center.text-4xl.text-white.dark_text-primary-500.-mb-4(
        v-if="canPlayAudio"
      )
        +icon-button.voicemail__play_pause.order-2.w-20.h-20(
          @click="toggleVoicemail"
        )
          .absolute.flex.items-center.justify-center.voicemail__play.auto-alpha-1.scale-100.auto-alpha-fast(
            :class="{ 'auto-alpha-0 !opacity-0 scale-50': playing }"
            icon="carbon:play-filled-alt"
          )
            uc-icon(icon="carbon:play-filled-alt")
          .absolute.flex.items-center.justify-center.voicemail__pause.auto-alpha-0.scale-50.auto-alpha-fast(
            :class="{ 'auto-alpha-1 !scale-100': playing }"
          )
            uc-icon(icon="carbon:pause-filled")

        +icon-button.w-16.h-16.order-1(@click="rewindVoicemail")
          uc-icon(icon="ion:play-back-sharp")

        +icon-button.w-16.h-16.order-3(@click="forwardVoicemail")
          uc-icon(icon="ion:play-forward-sharp")
  .voicemail__transcript.text-gray-500.dark_text-gray-50.prose-stack(
    class="mx-[2rem]"
    ref="transcriptRef"
    v-if="!audioDoc && !wordDoc && canPlayAudio && !suspend.reducedMotion"
  )
    uc-text.voicemail__transcript-text(
      :k="`${screen.id}.transcript`"
      :text="screen.transcript"
    )
  .voicemail__transcript.text-gray-500.dark_text-gray-50.prose-stack(
    class="mx-[2rem]"
    v-else-if="wordDoc || !canPlayAudio || suspend.reducedMotion"
  )
    uc-text.voicemail__transcript-text(
      :k="`${screen.id}.transcript`"
      :text="screen.transcript"
      uc-dont-read=""
    )
</template>

<script lang="ts" setup>
import { play, stop } from '../lib/audio';
import gsap from 'gsap';
import SplitText from 'gsap/SplitText';
import { setCompleted } from '../lib/SCORM';
import { onMounted, ref, watch, inject, unref, computed, nextTick } from 'vue';
import { useEventListener } from '@vueuse/core';
import drawWaveform, { drawSampleWaveform } from '../lib/drawWaveform';
import ScrollTrigger from 'gsap/ScrollTrigger';
import useSeek from '../lib/useSeek';
import bugAlert from '../lib/bugAlert';
import { CustomEase } from 'gsap/all';
import useSuspend from '../lib/useSuspend';
import expandUploadPath from '../lib/expandUploadPath';

const suspend = useSuspend();

gsap.registerPlugin(SplitText);

type VoicemailScreen = UcScreen & {
  'dont-unlock'?: boolean;
  id: string;
  voicemail: string;
  headshot: string;
  transcript: string;
};

const props = defineProps<{ screen: VoicemailScreen }>();

const sectionId = inject('sectionId') as string;
const sectionActive = inject('sectionActive');

watch(
  () => suspend.locale,
  locale => {
    if (unref(audioRef)) {
      resetVoicemail();
      updateProgress(0);
    }

    waveformDataURL.value = '';
    tl = null;
    canPlayAudio.value = false;
    createWaveformDataURL();
    initTranscriptAnim();
  }
);

// fade in/out the voicemail audio based on audio settings (don't stop playback though)
watch(
  () => suspend.mute,
  muted => {
    const audio = unref(audioRef);

    if (audio) {
      gsap.to(audio, {
        volume: muted ? 0 : 1
      });
    }
  }
);

watch(
  () => suspend.reducedMotion,
  reducedMotion => {
    if (playing.value) {
      pause();
    }

    resetVoicemail();

    if (!reducedMotion) {
      createWaveformDataURL();
      initTranscriptAnim();
    } else {
      completeScreen();
    }
  }
);

// is this voice mail screen active and muted, if so auto-complete (unless it's a voicemail-question screen)
watch(
  [sectionActive, () => suspend.mute || suspend.narrationMute],
  async ([a, m]) => {
    if (a && m) {
      await nextTick(); // wait for section--active class to get applied to the DOM

      const el = unref(elRef);
      const vmqScreen = el?.closest(
        '.section--active .screen--voicemail-question'
      );

      if (!vmqScreen) {
        setCompleted(sectionId);
      }
    }
  }
);

if (!props.screen.voicemail) {
  bugAlert(`Voicemail missing ID in #${sectionId}`);
}

const audioRef = ref<HTMLAudioElement>();
const canvasRef = ref<HTMLCanvasElement>();
const seekRef = ref<HTMLDivElement>();
const seekProgressRef = ref<HTMLDivElement>();
const seekProgressDotRef = ref<HTMLDivElement>();
const transcriptRef = ref<HTMLDivElement>();
const waveformDataURL = ref('');
const ended = ref(false);
const playing = ref(false);
const audioDoc = inject('audioDoc') as boolean;
const wordDoc = inject('wordDoc') as Ref<boolean>;
const elRef = ref<HTMLElement | null>(null);
const canPlayAudio = ref(false);
const audioSrc = ref(expandUploadPath(props.screen.voicemail));

let tl: gsap.core.Timeline;

const shorten = 0.875;
const ease = CustomEase.create(
  'custom',
  'M0,0 C0,0 0.105,0.051 0.198,0.144 0.478,0.424 1,1 1,1'
);

const createWaveformDataURL = async () => {
  // only draw it once, when ready
  if (waveformDataURL.value !== '') return;

  const canvas = canvasRef.value;
  if (!canvas) return;

  // Wait for the canvas to be properly sized and any animations to complete
  await waitForCanvasReady(canvas);

  // Double-check the canvas has valid dimensions after waiting
  const rect = canvas.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return;
  }

  let url = expandUploadPath(props.screen.voicemail);

  // attempt to draw the translated audio waveform
  if (suspend.locale !== 'en-GB') {
    url = url.replace(/\.mp3$/, `_${suspend.locale}.mp3`);

    // autoplay policy may break drawWaveform, or CORS/network issues may prevent loading
    const drawn = await drawWaveform({
      canvas: canvas,
      src: url,
      samples: 100,
      stroke: 2
    });

    if (drawn) {
      waveformDataURL.value = canvas.toDataURL();
      canPlayAudio.value = true;
      audioSrc.value = url;
      return;
    }
  }

  // fallback to the original audio file
  url = expandUploadPath(props.screen.voicemail);

  const drawn = await drawWaveform({
    canvas: canvas,
    src: url,
    samples: 100,
    stroke: 2
  });

  if (drawn) {
    waveformDataURL.value = canvas.toDataURL();
    canPlayAudio.value = true;
    audioSrc.value = url;
  } else {
    // Fallback to a fixed waveform pattern if audio loading failed
    const fallback = drawSampleWaveform(canvas, 2);
    if (fallback) {
      waveformDataURL.value = canvas.toDataURL();
      console.log('Drew sample waveform');
    }
    canPlayAudio.value = false;
    completeScreen();
  }
};

/**
 * Waits for the canvas element to reach its final size, accounting for CSS transitions/animations
 */
const waitForCanvasReady = (canvas: HTMLCanvasElement): Promise<void> => {
  return new Promise(resolve => {
    // First, wait for the next frame to ensure layout has been calculated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const rect = canvas.getBoundingClientRect();
        const initialWidth = rect.width;
        const initialHeight = rect.height;

        // If canvas already has valid dimensions, check if it's stable
        if (initialWidth > 0 && initialHeight > 0) {
          // Wait a bit longer to ensure any CSS transitions (duration-1000 = 1s) have completed
          // Check multiple times to ensure size is stable
          let stableCount = 0;
          const checkStability = () => {
            const currentRect = canvas.getBoundingClientRect();
            const isStable =
              Math.abs(currentRect.width - initialWidth) < 1 &&
              Math.abs(currentRect.height - initialHeight) < 1;

            if (isStable) {
              stableCount++;
              // Require 3 consecutive stable measurements (across ~50ms)
              if (stableCount >= 3) {
                resolve();
              } else {
                setTimeout(checkStability, 16); // ~60fps
              }
            } else {
              stableCount = 0;
              setTimeout(checkStability, 16);
            }
          };

          // Start checking after a short delay to allow transitions to start
          setTimeout(checkStability, 50);
        } else {
          // Canvas doesn't have dimensions yet, use ResizeObserver
          const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
              const { width, height } = entry.contentRect;
              if (width > 0 && height > 0) {
                // Wait a bit more to ensure size is stable after resize
                setTimeout(() => {
                  resizeObserver.disconnect();
                  resolve();
                }, 100); // Small delay to ensure transition completes
                return;
              }
            }
          });

          resizeObserver.observe(canvas);

          // Fallback timeout in case ResizeObserver never fires
          setTimeout(() => {
            resizeObserver.disconnect();
            resolve();
          }, 1500); // Max wait time (slightly longer than 1s transition)
        }
      });
    });
  });
};

ScrollTrigger.addEventListener(
  'refresh',
  createWaveformDataURL as ScrollTrigger.Callback
);

watch(canvasRef, createWaveformDataURL);

const loop = () => {
  if (playing.value) {
    updateSeekUI(getProgress());
    requestAnimationFrame(loop);
  }
};

watch(playing, () => {
  if (playing.value) loop();
});

const initTranscriptAnim = () => {
  if (wordDoc.value || audioDoc || !canPlayAudio.value) return;

  const transcriptEl = unref(transcriptRef);
  if (!transcriptEl) return;

  updateSeekUI(0);

  const splitTranscript = new SplitText(transcriptEl);

  tl = gsap
    .timeline({
      paused: true,
      clearProps: true
    })
    .fromTo(
      splitTranscript.chars,
      { opacity: 0 },
      {
        opacity: 1,
        ease: 'linear',
        stagger: { ease, each: 0.1, from: 0 },
        duration: 0.5,
        onComplete() {
          completeScreen();
        }
      }
    );
};

watch(transcriptRef, initTranscriptAnim);

const onEnded = () => {
  tl?.pause();
  ended.value = true;
  completeScreen();
  playQuestionAudio();
};

function playQuestionAudio() {
  const el = unref(elRef);
  const vmqScreen = el?.closest('.section--active .screen--voicemail-question');
  if (vmqScreen) {
    const vmq = vmqScreen?.querySelector('.voicemail-vmq');
    const audio = vmq?.getAttribute('data-audio');
    if (audio) {
      play(audio);
    }
  }
}

function completeScreen() {
  // TODO handle completion with useLockables
  // don't complete screen after the voicemail if we are in a vm-question template
  if (props.screen.question) return;
  if (props.screen['dont-unlock']) return;

  setCompleted(props.screen.id);
  setCompleted(sectionId);
}

// @ts-ignore - used in pug mixin
function toggleVoicemail() {
  const audio = unref(audioRef);
  if (!audio) return;

  if (!suspend.mute) {
    audio.volume = 1;
  }

  if (playing.value) {
    audio.pause();
  } else {
    audio.play();
  }
}

// @ts-ignore - used in pug mixin
function forwardVoicemail() {
  const audio = unref(audioRef);
  if (!audio) return;

  audio.currentTime = Math.min(audio.currentTime + 5, audio.duration);

  updateProgress();
}

// @ts-ignore - used in pug mixin
function rewindVoicemail() {
  const audio = unref(audioRef);
  if (!audio) return;

  audio.currentTime = Math.max(audio.currentTime - 5, 0);

  updateProgress();
}

function resetVoicemail() {
  const audio = unref(audioRef);
  if (!audio) return;
  pause();
  audio.currentTime = 0;
  updateProgress();
}

useEventListener(audioRef, 'play', () => {
  const audio = unref(audioRef);
  if (!audio) return;

  if (ended.value) {
    tl?.totalProgress(0);
    ended.value = false;
  }

  tl?.totalDuration(audio.duration * shorten);
  tl?.play();
  playing.value = true;

  // Stop screen content audio
  stop();
});

const pause = () => {
  const audio = unref(audioRef);
  if (!audio) return;

  tl?.pause();
  audio.pause();
  playing.value = false;
};

useEventListener(audioRef, 'pause', pause);

function updateSeekUI(v: number, { duration = 0 }: { duration?: number } = {}) {
  try {
    gsap.to([seekProgressRef.value, seekProgressDotRef.value], {
      xPercent: v * 100 - 100,
      duration
    });
  } catch (e) {
    console.error(e);
  }
}

function getProgress() {
  const audio = unref(audioRef);
  if (!audio) return 0;

  const { currentTime, duration } = audio;
  if (!duration) return 0;
  if (!currentTime) return 0;
  return currentTime / duration;
}

function updateProgress(
  newProgress?: number,
  { duration = 0.2 }: { duration?: number } = {}
) {
  const audio = unref(audioRef);
  if (!audio) return 0;
  const v = newProgress || getProgress();

  if (newProgress) {
    audio.currentTime = audio.duration * newProgress;
  }

  ended.value = false;
  updateSeekUI(v, { duration });

  gsap.to(tl, {
    duration: 0.2,
    progress: v
  });
}

useSeek(seekRef, updateProgress);

// pause audio on scroll
useEventListener(window, 'scrollsectionentered', () => {
  const audio = unref(audioRef);
  if (!audio) return;

  gsap.to(audio, {
    volume: 0,
    onComplete() {
      audio.pause();
      audio.volume = suspend.mute || suspend.narrationMute ? 0 : 1;
    }
  });
});
</script>

<style>
.voicemail__transcript-text {
  p {
    display: flex;
    flex-wrap: wrap;
    column-gap: 0.4ch;
  }
}
</style>
